/**
 * The web host.
 *
 * Everything browser-specific in `@anthra/platform` lives here and nowhere else.
 * `make check-native` fails if a `document.`, `localStorage` or `import.meta`
 * appears anywhere else in the package — that guard is the only thing that keeps
 * the shared code actually shareable, because the coupling always comes back one
 * convenient line at a time.
 */

import type { AppInfo, Host, KeyValueStore } from "./index"

const localStore: KeyValueStore = {
	get: key => {
		try {
			return Promise.resolve(localStorage.getItem(key))
		} catch {
			// Safari in private mode throws on access, and a Storage quota error
			// must not take down logging.
			return Promise.resolve(null)
		}
	},
	set: (key, value) => {
		try {
			localStorage.setItem(key, value)
		} catch {
			/* quota or private mode — non-fatal */
		}
		return Promise.resolve()
	},
	remove: key => {
		try {
			localStorage.removeItem(key)
		} catch {
			/* non-fatal */
		}
		return Promise.resolve()
	},
}

/**
 * In-memory only. See the note on `Host.secureStorage`: a browser has no secure
 * store, so the honest implementation is one that does not survive a reload.
 * The refresh token lives in an HttpOnly cookie instead.
 */
function memoryStore(): KeyValueStore {
	const data = new Map<string, string>()
	return {
		get: key => Promise.resolve(data.get(key) ?? null),
		set: (key, value) => {
			data.set(key, value)
			return Promise.resolve()
		},
		remove: key => {
			data.delete(key)
			return Promise.resolve()
		},
	}
}

export function webHost(): Host {
	const secure = memoryStore()

	return {
		name: "web",

		env: key => (import.meta.env as Record<string, string | undefined>)[key],

		storage: localStore,
		secureStorage: secure,

		onBackground: callback => {
			// `visibilitychange` and `pagehide`, not `unload`: `unload` is
			// unreliable on mobile Safari and blocks the bfcache. Records lost on
			// navigation are disproportionately the ones from a failing flow.
			const onVisibility = () => {
				if (document.visibilityState === "hidden") callback()
			}
			const onPageHide = () => callback()

			document.addEventListener("visibilitychange", onVisibility)
			window.addEventListener("pagehide", onPageHide)

			return () => {
				document.removeEventListener("visibilitychange", onVisibility)
				window.removeEventListener("pagehide", onPageHide)
			}
		},

		beacon: (url, body) => {
			// sendBeacon survives page teardown; fetch does not.
			if (typeof navigator.sendBeacon !== "function") return false
			try {
				return navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))
			} catch {
				return false
			}
		},

		installGlobalErrorHandler: handler => {
			window.addEventListener("error", event => {
				handler(event.error, {
					source: event.filename,
					line: event.lineno,
					column: event.colno,
				})
			})
			window.addEventListener("unhandledrejection", event => {
				handler(event.reason, { kind: "unhandledrejection" })
			})
		},

		appInfo: (): AppInfo => ({
			platform: "web",
			version:
				(import.meta.env as Record<string, string | undefined>).VITE_APP_VERSION ??
				"0.0.0-local",
			buildId:
				(import.meta.env as Record<string, string | undefined>).VITE_BUILD_ID ?? "local",
			locale: typeof navigator !== "undefined" ? navigator.language : "en-IN",
		}),
	}
}
